import { HTMLAttributes, ReactElement, SVGAttributes } from 'react';
import { RiGlobalLine } from '@remixicon/react';
import {
  AE,
  AU,
  BH,
  BR,
  CA,
  CH,
  CL,
  CN,
  DE,
  DK,
  ES,
  FR,
  GB,
  HK,
  ID,
  IE,
  IL,
  IN,
  IT,
  JP,
  KR,
  MX,
  MY,
  NL,
  NO,
  NZ,
  PL,
  QA,
  SE,
  SG,
  TH,
  US,
  ZA,
} from 'country-flag-icons/react/3x2';
import styles from './resources-page.module.css';

// country-flag-icons types its flag components against its own
// HTMLElement & SVGElement alias instead of SVGSVGElement — mirror that
// shape, otherwise the imported flags don't type-check.
type FlagElement = HTMLElement & SVGElement;
type FlagComponent = (props: HTMLAttributes<FlagElement> &
  SVGAttributes<FlagElement>) => ReactElement;

// AWS region -> ISO country of the region's data center. Explicit entries:
// the prefix says nothing (eu-west-1 is Ireland, eu-west-2 is London), and
// unknown/new regions degrade to the globe icon.
const AWS_REGION_COUNTRIES: Record<string, string> = {
  'us-east-1': 'US',
  'us-east-2': 'US',
  'us-west-1': 'US',
  'us-west-2': 'US',
  'us-gov-east-1': 'US',
  'us-gov-west-1': 'US',
  'ca-central-1': 'CA',
  'ca-west-1': 'CA',
  'sa-east-1': 'BR',
  'mx-central-1': 'MX',
  'eu-west-1': 'IE',
  'eu-west-2': 'GB',
  'eu-west-3': 'FR',
  'eu-central-1': 'DE',
  'eu-central-2': 'CH',
  'eu-north-1': 'SE',
  'eu-south-1': 'IT',
  'eu-south-2': 'ES',
  'il-central-1': 'IL',
  'ap-northeast-1': 'JP',
  'ap-northeast-2': 'KR',
  'ap-northeast-3': 'JP',
  'ap-southeast-1': 'SG',
  'ap-southeast-2': 'AU',
  'ap-southeast-3': 'ID',
  'ap-southeast-4': 'AU',
  'ap-southeast-5': 'MY',
  'ap-southeast-7': 'TH',
  'ap-south-1': 'IN',
  'ap-south-2': 'IN',
  'ap-east-1': 'HK',
  'me-south-1': 'BH',
  'me-central-1': 'AE',
  'af-south-1': 'ZA',
  'cn-north-1': 'CN',
  'cn-northwest-1': 'CN',
};

// Azure location -> ISO country (names normalized: lowercase, no spaces).
const AZURE_REGION_COUNTRIES: Record<string, string> = {
  westeurope: 'NL',
  northeurope: 'IE',
  uksouth: 'GB',
  ukwest: 'GB',
  francecentral: 'FR',
  francesouth: 'FR',
  germanywestcentral: 'DE',
  germanynorth: 'DE',
  switzerlandnorth: 'CH',
  switzerlandwest: 'CH',
  norwayeast: 'NO',
  norwaywest: 'NO',
  swedencentral: 'SE',
  denmarkeast: 'DK',
  denmarkwest: 'DK',
  polandcentral: 'PL',
  italynorth: 'IT',
  spaincentral: 'ES',
  eastus: 'US',
  eastus2: 'US',
  eastus2euap: 'US',
  westus: 'US',
  westus2: 'US',
  westus3: 'US',
  centralus: 'US',
  northcentralus: 'US',
  southcentralus: 'US',
  westcentralus: 'US',
  canadacentral: 'CA',
  canadaeast: 'CA',
  brazilsouth: 'BR',
  brazilsoutheast: 'BR',
  mexicocentral: 'MX',
  japaneast: 'JP',
  japanwest: 'JP',
  koreacentral: 'KR',
  koreasouth: 'KR',
  eastasia: 'HK',
  southeastasia: 'SG',
  centralindia: 'IN',
  southindia: 'IN',
  westindia: 'IN',
  indonesiacentral: 'ID',
  malaysiawest: 'MY',
  australiaeast: 'AU',
  australiasoutheast: 'AU',
  australiacentral: 'AU',
  australiacentral2: 'AU',
  newzealandnorth: 'NZ',
  chilecentral: 'CL',
  uaenorth: 'AE',
  uaecentral: 'AE',
  qatarcentral: 'QA',
  israelcentral: 'IL',
  southafricanorth: 'ZA',
  southafricawest: 'ZA',
};

const FLAGS: Record<string, FlagComponent> = {
  AE,
  AU,
  BH,
  BR,
  CA,
  CH,
  CL,
  CN,
  DE,
  DK,
  ES,
  FR,
  GB,
  HK,
  ID,
  IE,
  IL,
  IN,
  IT,
  JP,
  KR,
  MX,
  MY,
  NL,
  NO,
  NZ,
  PL,
  QA,
  SE,
  SG,
  TH,
  US,
  ZA,
};

const normalizeAzure = (region: string) => region.toLowerCase().replace(/[^a-z]/g, '');

export const regionToCountry = (region: string): string | undefined =>
  AWS_REGION_COUNTRIES[region.toLowerCase()] ??
  AZURE_REGION_COUNTRIES[normalizeAzure(region)];

/**
 * Region shown as a monospace chip with the SVG flag of the region's
 * country (shared language with the account header region chips). Unknown
 * regions and the pseudo-region "global" get a globe icon instead of a
 * wrong flag.
 */
export function RegionChip({ region }: { region: string }) {
  const country = regionToCountry(region);
  const Flag = country ? FLAGS[country] : undefined;

  return (
    <span
      className={styles.regionChip}
      title={country ? `${region} (${country})` : region}
    >
      {Flag ? (
        <Flag className={styles.regionFlag} aria-hidden="true" />
      ) : (
        <RiGlobalLine size={12} className={styles.regionFlag} aria-hidden="true" />
      )}
      {region}
    </span>
  );
}
